/**
 * p80-experiment-tracking.js
 * ---------------------------------------------------------------------------
 * Purpose:
 *   Fires custom analytics events for the "collection_filters_2026_07" A/B
 *   experiment, comparing collection filter UX across two published themes:
 *     - main = control    (data-p80-variant-id="control")
 *     - Surplus City [Dev - J] - Filter Buttons A v2 = treatment  (data-p80-variant-id="treatment")
 *
 * How it fits together:
 *   theme.liquid sets data-p80-experiment-id / data-p80-variant-id /
 *   data-p80-collection-handle on <html> (collection pages only)
 *     -> this script reads those attributes and calls
 *        Shopify.analytics.publish("p80:<event>", {...})
 *     -> the store's Custom Pixel (Settings > Customer events) subscribes to
 *        each "p80:<event>" name and forwards the payload into
 *        window.dataLayer.push() for GTM/GA4
 *
 *   This script has real DOM access; the Custom Pixel does not (sandboxed
 *   iframe) — publish/subscribe is the only bridge between the two.
 *
 * IMPORTANT — keep in sync across both themes:
 *   This file must be byte-for-byte identical in Theme X and Theme Y.
 *   The only per-theme difference lives in theme.liquid
 *   (data-p80-variant-id="control" vs "treatment"). If you edit tracking
 *   logic in one theme, copy the same change to the other or the experiment
 *   data will no longer be comparable.
 *
 * Events published (must match the pixel's forwardEvent() list exactly):
 *   p80:experiment_impression      - once per collection page load
 *   p80:collection_filter_change   - checkbox filter checked/unchecked
 *   p80:collection_filter_clear    - "Remove All" clicked
 *   p80:collection_filter_results  - product grid re-rendered after a filter action
 *   p80:collection_product_click   - a product link inside the grid was clicked
 *
 * Selectors this script depends on (confirmed identical in both themes as of
 * 2026-07-30 — re-verify here if either theme's filter markup changes):
 *   input[name^="filter."]        - all filter checkboxes
 *   .active-facets__button-remove - "Remove All" button
 *   #product-grid                 - product grid container (MutationObserver target)
 *   #ProductCountDesktop           - visible product count, desktop
 *   .mobile-facets__count         - visible product count, mobile
 *
 * Load order: place near </body> (or use `defer`), after other theme JS.
 * No page-type conditional needed around the <script> tag itself — the
 * script no-ops safely via the data-p80-experiment-id guard below on any
 * page that isn't part of the experiment.
 * ---------------------------------------------------------------------------
 */

(function () {
  const html = document.documentElement;
  const experimentId = html.dataset.p80ExperimentId;
  if (!experimentId) return; // not a collection page in the experiment

  const variantId = html.dataset.p80VariantId;
  const collectionHandle = html.dataset.p80CollectionHandle;
  const grid = document.querySelector('#product-grid');

  function publish(eventName, extra) {
    Shopify.analytics.publish(
      eventName,
      Object.assign(
        {
          experiment_id: experimentId,
          variant_id: variantId,
          collection_handle: collectionHandle
        },
        extra
      )
    );
  }

  function activeFilterCount() {
    return document.querySelectorAll('input[name^="filter."]:checked').length;
  }

  // Parses "91 of 91 products" -> 91. Falls back to mobile count if desktop node is hidden/absent.
  function getResultCount() {
    const desktop = document.querySelector('#ProductCountDesktop');
    const mobile = document.querySelector('.mobile-facets__count');
    const source = (desktop?.offsetParent !== null ? desktop : mobile) || desktop || mobile;
    if (!source) return null;
    const match = source.textContent.match(/(\d+)\s+of\s+(\d+)/i);
    return match ? parseInt(match[1], 10) : null;
  }

  function resultBucket(count) {
    if (count === null) return 'unknown';
    if (count === 0) return '0';
    if (count <= 10) return '1-10';
    if (count <= 50) return '11-50';
    return '50+';
  }

  // 1. Impression — once per page load
  publish('p80:experiment_impression', {});

  // 2. Filter change (checkbox add/remove, including quick-filter buttons
  //    since those check the box programmatically and dispatch change)
  document.addEventListener('change', (event) => {
    const input = event.target.closest('input[name^="filter."]');
    if (!input) return;
    publish('p80:collection_filter_change', {
      filter_action: input.checked ? 'add' : 'remove',
      filter_id: input.name,
      filter_value: input.value,
      active_filter_count: activeFilterCount()
    });
  });

  // 3. Clear all
  document.addEventListener('click', (event) => {
    const clearAll = event.target.closest('.active-facets__button-remove');
    if (!clearAll) return;
    publish('p80:collection_filter_clear', {
      active_filter_count_before_clear: activeFilterCount()
    });
  });

  // 4. Results updated — fires after the AJAX-rerendered grid settles
  if (grid) {
    let debounce;
    new MutationObserver(() => {
      clearTimeout(debounce);
      debounce = setTimeout(() => {
        const resultCount = getResultCount();
        publish('p80:collection_filter_results', {
          result_count: resultCount,
          result_bucket: resultBucket(resultCount),
          filtered: activeFilterCount() > 0
        });
      }, 150);
    }).observe(grid, {childList: true, subtree: true});
  }

  // 5. Product click
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href*="/products/"]');
    if (!link || !grid?.contains(link)) return;
    publish('p80:collection_product_click', {
      product_path: new URL(link.href).pathname
    });
  });
})();
